import type { SocialProvider } from '@/lib/apis/types'
import { isValidSocialSessionId } from './social-logout.ts'

export const SOCIAL_PROVIDER_ORDER = [
  'APPLE',
  'GOOGLE',
  'MICROSOFT',
] as const satisfies readonly SocialProvider[]

export const SOCIAL_PROVIDER_DETAILS: Record<
  SocialProvider,
  {
    authJsId: string
    connection: string
    label: string
  }
> = {
  APPLE: {
    authJsId: 'auth0-apple',
    connection: 'apple',
    label: 'Apple',
  },
  GOOGLE: {
    authJsId: 'auth0-google',
    connection: 'google-oauth2',
    label: 'Google',
  },
  MICROSOFT: {
    authJsId: 'auth0-microsoft',
    connection: 'windowslive',
    label: 'Microsoft',
  },
}

export type SocialAuthenticationMode = 'authenticate' | 'challenge'

export type PrepareSocialAuthenticationInput = {
  nonce: string
  provider: SocialProvider
  popup: boolean
  returnTo: string
  errorTo?: string
  continuationToken?: string
  challenge?: {
    id: string
    token: string
  }
}

export type SocialAuthenticationFlow = {
  nonce: string
  provider: SocialProvider
  mode: SocialAuthenticationMode
  popup: boolean
  returnTo: string
  errorTo: string
  continuationToken?: string
  challenge?: {
    id: string
    token: string
  }
  pkce?: {
    state: string
    verifier: string
  }
}

export type SocialAuthenticationResult = {
  ok: boolean
  nonce: string
  mode: SocialAuthenticationMode
  provider: SocialProvider
  error?: SocialAuthenticationErrorCode
  proofToken?: string | null
}

export const SOCIAL_AUTHENTICATION_MESSAGE =
  'nextclinic:social-authentication-result'
export const SOCIAL_AUTHENTICATION_ACKNOWLEDGEMENT =
  'nextclinic:social-authentication-acknowledgement'

export function socialAuthenticationChannelName(nonce: string) {
  return `nextclinic:social-authentication:${nonce}`
}

export const SOCIAL_AUTHENTICATION_ERRORS = [
  'SOCIAL_EMAIL_REQUIRED',
  'SOCIAL_EMAIL_NOT_VERIFIED',
  'SOCIAL_REAUTHENTICATION_REQUIRED',
  'SOCIAL_PROVIDER_ACCOUNT_CONFLICT',
  'INVALID_AUTHENTICATION_CHALLENGE',
  'EXPIRED_AUTHENTICATION_CHALLENGE',
  'REPLAYED_AUTHENTICATION_CHALLENGE',
  'EXPLICITLY_UNLINKED_IDENTITY',
  'LAST_AUTHENTICATION_METHOD',
  'SOCIAL_ROLE_CONFLICT',
  'DUPLICATE_CANONICAL_ACCOUNT',
  'UNSUPPORTED_SOCIAL_PROVIDER',
  'INVALID_SOCIAL_IDENTITY_CLAIM',
  'IDENTITY_CONFIGURATION_ERROR',
  'UNSUPPORTED_SOCIAL_CLIENT',
  'SOCIAL_AUTHENTICATION_CANCELLED',
  'SOCIAL_AUTHENTICATION_FAILED',
  'POPUP_BLOCKED',
] as const

export type SocialAuthenticationErrorCode =
  (typeof SOCIAL_AUTHENTICATION_ERRORS)[number]

export function isSocialProvider(value: unknown): value is SocialProvider {
  return SOCIAL_PROVIDER_ORDER.includes(value as SocialProvider)
}

export function isPrepareSocialAuthenticationInput(
  value: unknown,
): value is PrepareSocialAuthenticationInput {
  if (!value || typeof value !== 'object') return false

  const input = value as Record<string, unknown>
  const challenge = input.challenge

  return (
    typeof input.nonce === 'string' &&
    isSocialProvider(input.provider) &&
    typeof input.popup === 'boolean' &&
    typeof input.returnTo === 'string' &&
    (input.errorTo === undefined || typeof input.errorTo === 'string') &&
    (input.continuationToken === undefined ||
      typeof input.continuationToken === 'string') &&
    (challenge === undefined ||
      (Boolean(challenge) &&
        typeof challenge === 'object' &&
        typeof (challenge as Record<string, unknown>).id === 'string' &&
        typeof (challenge as Record<string, unknown>).token === 'string'))
  )
}

export function isSocialAuthenticationErrorCode(
  value: unknown,
): value is SocialAuthenticationErrorCode {
  return SOCIAL_AUTHENTICATION_ERRORS.includes(
    value as SocialAuthenticationErrorCode,
  )
}

export function socialProviderLabel(provider: SocialProvider) {
  return SOCIAL_PROVIDER_DETAILS[provider].label
}

export function socialAuthenticationErrorMessage(
  error: SocialAuthenticationErrorCode | string | null | undefined,
) {
  switch (error) {
    case 'SOCIAL_EMAIL_REQUIRED':
    case 'SOCIAL_EMAIL_NOT_VERIFIED':
      return 'That account does not have a verified email address. Try another account or sign in with email and password.'
    case 'SOCIAL_REAUTHENTICATION_REQUIRED':
      return 'Please sign in with that provider again to continue.'
    case 'SOCIAL_PROVIDER_ACCOUNT_CONFLICT':
      return 'That provider account is already linked to another NextClinic account.'
    case 'INVALID_AUTHENTICATION_CHALLENGE':
    case 'EXPIRED_AUTHENTICATION_CHALLENGE':
    case 'REPLAYED_AUTHENTICATION_CHALLENGE':
      return 'That confirmation has expired. Please start again.'
    case 'EXPLICITLY_UNLINKED_IDENTITY':
      return 'This sign-in was previously unlinked. Sign in another way, then link it again from your profile.'
    case 'LAST_AUTHENTICATION_METHOD':
      return 'You need to keep at least one way to sign in.'
    case 'SOCIAL_ROLE_CONFLICT':
      return 'Social sign-in is only available for patient accounts.'
    case 'DUPLICATE_CANONICAL_ACCOUNT':
      return 'We found more than one account with this email. Please contact support@nextclinic.com.au.'
    case 'UNSUPPORTED_SOCIAL_PROVIDER':
      return 'That sign-in option is not available.'
    case 'INVALID_SOCIAL_IDENTITY_CLAIM':
      return 'We could not verify that account. Please try again.'
    case 'IDENTITY_CONFIGURATION_ERROR':
    case 'UNSUPPORTED_SOCIAL_CLIENT':
      return 'Social sign-in is temporarily unavailable. Please try another sign-in method.'
    case 'POPUP_BLOCKED':
      return 'Allow pop-ups for NextClinic, then try again.'
    case 'SOCIAL_AUTHENTICATION_CANCELLED':
      return 'Sign-in was cancelled. Please try again when you’re ready.'
    default:
      return 'We could not sign you in. Please try again.'
  }
}

export function socialAuthenticationErrorFromAuth0Description(
  description: string | null | undefined,
): SocialAuthenticationErrorCode | null {
  const match = description?.match(/(?:^|\s)NEXTCLINIC_([A-Z0-9_]+):?/)
  if (!match) return null
  const code = match[1]
  return isSocialAuthenticationErrorCode(code) ? code : null
}

export function socialAuthenticationErrorFromOAuth(
  oauthError: string | null | undefined,
  description: string | null | undefined,
): SocialAuthenticationErrorCode {
  return (
    socialAuthenticationErrorFromAuth0Description(description) ??
    (oauthError === 'access_denied'
      ? 'SOCIAL_AUTHENTICATION_CANCELLED'
      : 'SOCIAL_AUTHENTICATION_FAILED')
  )
}

export function isValidSocialChallengeCallback(
  flow: SocialAuthenticationFlow | null,
  state: string | null,
  code: string | null,
) {
  return Boolean(
    flow?.mode === 'challenge' &&
    flow.challenge &&
    flow.pkce &&
    code &&
    state &&
    state === flow.pkce.state,
  )
}

type SocialAuthenticationMessageLike = {
  type?: unknown
  nonce?: unknown
  result?: { nonce?: unknown; ok?: unknown } | null
}

export function isExpectedSocialAuthenticationPayload(
  data: SocialAuthenticationMessageLike | null | undefined,
  nonce: string,
) {
  return (
    data?.type === SOCIAL_AUTHENTICATION_MESSAGE &&
    data.nonce === nonce &&
    data.result?.nonce === nonce &&
    typeof data.result.ok === 'boolean'
  )
}

export function isExpectedSocialAuthenticationMessage(
  event: {
    origin: string
    source: unknown
    data?: SocialAuthenticationMessageLike | null
  },
  expectedSource: unknown,
  expectedOrigin: string,
  nonce: string,
) {
  return (
    event.origin === expectedOrigin &&
    event.source === expectedSource &&
    isExpectedSocialAuthenticationPayload(event.data, nonce)
  )
}

export function isCanonicalSocialSession(
  session: {
    tokenStrategy?: unknown
    socialProvider?: unknown
    socialSessionId?: unknown
    accessToken?: unknown
    user?: { id?: unknown; roles?: unknown } | null
  } | null,
  provider: SocialProvider,
) {
  return Boolean(
    session?.tokenStrategy === 'social' &&
    session.socialProvider === provider &&
    isValidSocialSessionId(session.socialSessionId) &&
    typeof session.accessToken === 'string' &&
    session.accessToken &&
    typeof session.user?.id === 'string' &&
    session.user.id &&
    Array.isArray(session.user.roles) &&
    session.user.roles.includes('patient'),
  )
}

export function enabledSocialProviders(): SocialProvider[] {
  const flags: Record<SocialProvider, boolean> = {
    APPLE: process.env.NEXT_PUBLIC_SOCIAL_LOGIN_APPLE_ENABLED === 'true',
    GOOGLE: process.env.NEXT_PUBLIC_SOCIAL_LOGIN_GOOGLE_ENABLED === 'true',
    MICROSOFT:
      process.env.NEXT_PUBLIC_SOCIAL_LOGIN_MICROSOFT_ENABLED === 'true',
  }

  return SOCIAL_PROVIDER_ORDER.filter((provider) => flags[provider])
}

export function socialProviderFromAuthJsId(
  providerId: string | undefined,
): SocialProvider | null {
  return (
    SOCIAL_PROVIDER_ORDER.find(
      (provider) => SOCIAL_PROVIDER_DETAILS[provider].authJsId === providerId,
    ) ?? null
  )
}
